import { type MouseEvent, type ReactNode, useEffect, useState } from "react";

import { tv, type VariantProps } from "tailwind-variants";

import { chain, cn } from "@virtual-brain-lib/frontend-design-system/utils";

import { Button, type ButtonProps } from "./Button";
import { Span } from "./Span";

export const tabsVariants = tv({
	base: "flex w-fit gap-2 py-2",
	variants: {
		size: {
			sm: "px-1",
			md: "px-2",
			lg: "px-3",
			xl: "px-3",
		},
		orientation: {
			horizontal: "flex-row",
			vertical: "flex-col",
		},
		textAlign: {
			center: "items-center",
			left: "items-start",
		},
		variant: {
			solid: "",
			underline: "",
			bordered: "rounded-md border border-primary bg-primary-light",
		},
	},
	defaultVariants: {
		size: "md",
		orientation: "horizontal",
		textAlign: "center",
	},
});

type TabsVariant = VariantProps<typeof tabsVariants>;

export type TabsItem<TMetadata = unknown, TTabKey extends string = string> = {
	readonly key: TTabKey;
	isHidden?: boolean;
	metadata?: TMetadata;
} & (
	| (Pick<
			ButtonProps,
			| "onClick"
			| "disabled"
			| "leftIcon"
			| "leftComponent"
			| "rightIcon"
			| "rightComponent"
			| "color"
			| "onMouseEnter"
			| "onMouseLeave"
			| "tooltip"
			| "linkProps"
			| "variant"
	  > & {
			readonly title?: string;
			readonly afterButtonText?: string;
	  })
	| {
			readonly customItem: ReactNode;
	  }
);

export type TabsProps<
	TMetadata = unknown,
	TTabs extends Array<TabsItem<TMetadata>> = Array<TabsItem<TMetadata>>,
	TTabKey = TTabs[number]["key"],
> = {
	readonly tabs: TTabs;
	readonly color?: ButtonProps["color"];
	readonly className?: string;
	readonly classNames?: {
		tabs?: string;
		tabItem?: string;
		tabItemBase?: string;
		tabItemWrapper?: string;
	};
	readonly selectedKey?: NoInfer<TTabKey> | false;
	readonly onSelectedTabChange?: (selectedTabKey: TTabKey) => void;
	readonly onMouseEnterTab?: (
		selectedTabKey: TTabs[number],
		event: MouseEvent<HTMLDivElement>,
	) => void;
	readonly onMouseLeaveTab?: (
		selectedTabKey: TTabs[number],
		event: MouseEvent<HTMLDivElement>,
	) => void;
} & Pick<TabsVariant, "size" | "orientation" | "textAlign" | "variant">;

export function isInTab<
	TTabs extends TabsItem[] = TabsItem[],
	TTabKey extends string = TTabs[number]["key"],
>(tabs: TTabs, tabKey: string): tabKey is TTabKey {
	if (!tabKey) {
		return false;
	}

	return Boolean(tabs.find((tab) => tab.key === tabKey));
}

export function Tabs<
	TMetadata = unknown,
	TTabs extends Array<TabsItem<TMetadata>> = Array<TabsItem<TMetadata>>,
	TTabKey = TTabs[number]["key"],
>({
	variant = "solid",
	tabs,
	size,
	orientation,
	color: tabColor,
	textAlign,
	selectedKey,
	onSelectedTabChange,
	onMouseEnterTab,
	onMouseLeaveTab,
	className,
	classNames,
}: TabsProps<TMetadata, TTabs, TTabKey>) {
	const [selectedTabKey, setSelectedTabKey] = useState<TTabKey | undefined>(
		selectedKey || undefined,
	);

	const container = tabsVariants({
		size,
		orientation,
		textAlign,
		variant,
		className: cn(className, classNames?.tabs),
	});

	useEffect(() => {
		setSelectedTabKey(selectedKey || undefined);
	}, [selectedKey]);

	const handleSelectTab = (tabKey: TTabKey) => {
		setSelectedTabKey(tabKey);
		onSelectedTabChange?.(tabKey);
	};

	return (
		<div className={container}>
			{tabs
				.filter((tab) => !tab.isHidden)
				.map((tab) => {
					if ("customItem" in tab) {
						return (
							<div
								key={tab.key}
								className={cn(classNames?.tabItem)}
								onClick={() => {
									handleSelectTab(tab.key as TTabKey);
								}}
							>
								{tab.customItem}
							</div>
						);
					}

					const {
						key,
						title,
						onClick,
						color = "primary",
						afterButtonText,
						...buttonProps
					} = tab;

					if (key.startsWith("separator")) {
						return <div key={key} className="h-px w-full bg-primary-border" />;
					}

					return (
						<div
							key={key}
							className={cn(
								"relative flex flex-col items-center justify-center gap-1 rounded-lg",
								classNames?.tabItem,
							)}
							onMouseEnter={(event) => onMouseEnterTab?.(tab, event)}
							onMouseLeave={(event) => onMouseLeaveTab?.(tab, event)}
						>
							<Button
								{...buttonProps}
								data-is-selected={selectedTabKey === tab.key}
								classNames={{
									wrapper: cn(
										variant === "underline" &&
											"relative after:absolute after:right-0 after:bottom-0 after:left-0 after:h-[2px] after:origin-center after:scale-x-0 after:rounded-none after:bg-current after:transition-transform data-[is-selected=true]:after:scale-x-100",
										classNames?.tabItemWrapper,
									),
									base: classNames?.tabItemBase,
								}}
								isDisableHover={variant === "underline"}
								variant={
									buttonProps.variant ??
									(variant === "underline" || selectedTabKey !== key
										? "ghost"
										: "solid")
								}
								size={size}
								color={tabColor ?? color}
								className="h-fit w-full"
								onClick={chain(onClick, () => {
									handleSelectTab(key as TTabKey);
								})}
							>
								{title}
							</Button>
							{afterButtonText && (
								<Span
									variant="body-md"
									className="cursor-pointer"
									onClick={() => {
										handleSelectTab(key as TTabKey);
									}}
								>
									{afterButtonText}
								</Span>
							)}
						</div>
					);
				})}
		</div>
	);
}
